//********************** View and edit general Property data **********************/

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { IPropertySettings } from '@/modules/settings/property/types';
import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import axiosClient from '@/shared/lib/axios';
import { IGeneral } from '@/shared/types';

//  get property
const getProperty = async (
  entityId: number | undefined
): Promise<IPropertySettings.IProperty> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/settings/company/property/${entityId}`
  );
  return data;
};

export const useGetProperty = (entityId: number | undefined) => {
  return useQuery({
    queryKey: ['getProperty', entityId],
    queryFn: () => getProperty(entityId),
    enabled: !!entityId,
  });
};

//  edit property
const updateProperty = async ({
  entityId,
  data,
}: IPropertySettings.IUpdatePropertyProps) => {
  const response = await axiosClient.patch(
    `/company/portalInsights/settings/company/property/${entityId}`,
    data
  );
  return response.data;
};

export const useUpdateProperty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProperty,
    onSuccess: () => {
      queryClient.invalidateQueries(['getProperty']);
      queryClient.invalidateQueries(['getEntities']);
    },
  });
};

//********************** Property Crops **********************/

//  Get property crops
const getPropertyCrops = async (
  params: IPropertySettings.IGetPropertyCropsParams
) => {
  const { data } =
    await axiosClient.get<IPropertySettings.IGetPropertyCropsResponse>(
      `/company/portalInsights/settings/company/property/crops`,
      { params }
    );
  //  Transorm the schema to the one used by the infinite select field component
  return {
    items: data.propertyCrops,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.totalCount / params.pageSize),
    totalItems: data.totalCount,
    nextPage:
      params?.pageNumber * params?.pageSize < data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetPropertyCrops = ({
  propertyId,
  pageNumber,
  pageSize,
}: IPropertySettings.IGetPropertyCropsParams) => {
  //  useInfiniteQuery because we need infinite scroll in the select field, so we handle pagination differently in the table
  return useInfiniteQuery({
    queryKey: ['getPropertyCrops', propertyId, { pageNumber, pageSize }],
    queryFn: ({ pageParam }) =>
      getPropertyCrops({
        propertyId,
        pageNumber: pageParam ?? pageNumber,
        pageSize,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!propertyId && !!pageNumber && !!pageSize,
  });
};

//  Create new property crop
const createPropertyCrop = async (
  cropData: IPropertySettings.ICreatePropertyCropDTO
): Promise<IGeneral.ICropSchema[]> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/settings/company/property/crops`,
    cropData
  );
  return data;
};
export const useCreatePropertyCrop = () => {
  return useMutation({
    mutationFn: createPropertyCrop,
  });
};

//********************** Property Fields **********************/

//  Get Property Fields
const getPropertyFields = async (
  params: IPropertySettings.IGetPropertyCropsParams
): Promise<IPropertySettings.IGetPropertyFieldsResponse> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/settings/company/property/fields`,
    {
      params,
    }
  );
  return data;
};

export const useGetPropertyFields = (
  params: IPropertySettings.IGetPropertyFieldsParams,
  options: { enabled: boolean } = { enabled: true }
) => {
  return useQuery({
    queryKey: ['getPropertyFields', params],
    queryFn: () => getPropertyFields(params),
    enabled:
      options.enabled &&
      !!params.pageNumber &&
      !!params.pageSize &&
      !!params.propertyId,
  });
};

//  Create property field
const createPropertyField = async (
  propertyData: IPropertySettings.ICreateEditFieldDTO
): Promise<IPropertySettings.IPropertyField> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/settings/company/property/field`,
    propertyData
  );
  return data;
};

export const useCreatePropertyField = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPropertyField,
    onSuccess: () => {
      queryClient.invalidateQueries(['getPropertyFields']);
      queryClient.invalidateQueries(['getPropertyCrops']);
      queryClient.invalidateQueries(['getHomeFields']);
      queryClient.invalidateQueries(['getFields']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
    },
  });
};

//  Archive field
const archivePropertyField = async (
  object: IPropertySettings.IFieldIdentifier
) => {
  const { data } = await axiosClient.patch(
    `/company/portalInsights/settings/company/property/field/archive`,
    object
  );
  return data;
};

export const useArchivePropertyField = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: archivePropertyField,
    onSuccess: () => queryClient.invalidateQueries(['getPropertyFields']),
  });
};

//  get Field details
const getPropertyFieldDetails = async (
  params: IPropertySettings.IGetFieldDetailsParams
): Promise<{ propertyField: IPropertySettings.IPropertyField }> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/settings/company/property/field',
    { params }
  );
  return data;
};

export const useGetPropertyFieldDetails = (
  params: IPropertySettings.IGetFieldDetailsParams
) => {
  return useQuery({
    queryKey: ['getPropertyFieldDetails', params],
    queryFn: () => getPropertyFieldDetails(params),
    enabled: !!params.fieldId && !!params.propertyId,
  });
};

//  Update field
const updateFieldDetails = async (
  obj: IPropertySettings.ICreateEditFieldDTO
) => {
  const { data } = await axiosClient.patch(
    '/company/portalInsights/settings/company/property/field',
    obj
  );
  return data;
};

export const useUpdateFieldDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateFieldDetails,
    onSuccess: () => queryClient.invalidateQueries(['getPropertyFieldDetails']),
  });
};

//********************** Custom Crops **********************/

//  get Suggested Custom crops
const getSuggestedCustomCrops = async (
  params: IPropertySettings.IGetSuggestedCustomCropsParams
) => {
  const { data } =
    await axiosClient.get<IPropertySettings.IGetSuggestedCustomCropsResponse>(
      `/company/portalInsights/crops/crop-names`,
      { params }
    );
  const totalPages = Math.ceil(data.totalCount / 10) ?? 1;
  return {
    items: data.cropNames.map((item) => {
      return {
        name: item.cropName,
        alreadyExists: item.alreadyExists,
      };
    }),
    totalCount: data.totalCount,
    totalItems: data.totalItems,
    currentPage: data.currentPage ?? 1,
    totalPages,
    nextPage:
      params.pageNumber * 10 < data?.totalCount
        ? params.pageNumber + 1
        : undefined,
  };
};

export const useGetSuggestedCustomCrops = (
  params: IPropertySettings.ISuggestedCustomCropsParams
) => {
  return useInfiniteQuery({
    queryKey: ['getSuggestedCustomCrops', params],
    queryFn: ({ pageParam }) =>
      getSuggestedCustomCrops({
        ...params,
        pageSize: 10,
        pageNumber: pageParam ?? 1,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!params.entityId && !!params.search,
  });
};

//  create Custom crop
const createCustomCrops = async (
  params: IPropertySettings.ICreateCustomCropSchema
) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/crops/custom`,
    params
  );
  return data;
};

export const useCreateCustomCrops = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createCustomCrops'],
    mutationFn: createCustomCrops,
    onSuccess: () => {
      queryClient.invalidateQueries(['getPropertyCrops']);
    },
  });
};

//********************** Custom Variety **********************/

//  get Suggested Custom variety
const getSuggestedCustomVariety = async (
  params: IPropertySettings.IGetSuggestedCustomVarietyParams
) => {
  const { data } =
    await axiosClient.get<IPropertySettings.IGetSuggestedCustomVarietyResponse>(
      `/company/portalInsights/varieties/variety-names`,
      { params }
    );
  const totalPages = Math.ceil(data.totalCount / 10) ?? 1;
  return {
    items: data.varietyNames.map((item) => {
      return {
        name: item.varietyName,
        alreadyExists: item.alreadyExists,
      };
    }),
    totalCount: data.totalCount,
    totalItems: data.totalItems,
    currentPage: data.currentPage ?? 1,
    totalPages,
    nextPage:
      params.pageNumber * 10 < data?.totalCount
        ? params.pageNumber + 1
        : undefined,
  };
};

export const useGetSuggestedCustomVariety = (
  params: IPropertySettings.ISuggestedCustomOperationsParams
) => {
  return useInfiniteQuery({
    queryKey: ['getSuggestedCustomVariety', params],
    queryFn: ({ pageParam }) =>
      getSuggestedCustomVariety({
        ...params,
        pageSize: 10,
        pageNumber: pageParam ?? 1,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!params.entityId && !!params.search && !!params.cropId,
  });
};

//  create Custom variety
const createCustomVariety = async (
  params: IPropertySettings.ICreateCustomVarietySchema
) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/varieties/custom`,
    params
  );
  return data;
};

export const useCreateCustomVariety = () => {
  return useMutation({
    mutationKey: ['createCustomVariety'],
    mutationFn: createCustomVariety,
  });
};

//********************** Custom Operations **********************/

//  get Suggested Custom Operations
const getSuggestedCustomOperations = async (
  params: IPropertySettings.IGetSuggestedCustomOperationsParams
) => {
  const { data } =
    await axiosClient.get<IPropertySettings.IGetSuggestedCustomOperationsResponse>(
      `/company/portalInsights/operations/task-names`,
      { params }
    );
  const totalPages = Math.ceil(data.totalCount / 10) ?? 1;
  return {
    items: data.taskNames.map((item) => {
      return {
        name: item.taskName,
        alreadyExists: item.alreadyExists,
      };
    }),
    totalCount: data.totalCount,
    totalItems: data.totalItems,
    currentPage: data.currentPage ?? 1,
    totalPages,
    nextPage:
      params.pageNumber * 10 < data?.totalCount
        ? params.pageNumber + 1
        : undefined,
  };
};

export const useGetSuggestedCustomOperations = (
  params: IPropertySettings.ISuggestedCustomOperationsParams
) => {
  return useInfiniteQuery({
    queryKey: ['getSuggestedCustomOperations', params],
    queryFn: ({ pageParam }) =>
      getSuggestedCustomOperations({
        ...params,
        pageSize: 10,
        pageNumber: pageParam ?? 1,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!params.cropId && !!params.entityId && !!params.search,
  });
};

//  create Custom Operations
const createCustomOperations = async (
  params: IPropertySettings.ICreateCustomOperationSchema
) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/operations/custom-operation`,
    params
  );
  return data;
};

export const useCreateCustomOperations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createCustomOperations'],
    mutationFn: createCustomOperations,
    onSuccess: () => {
      queryClient.invalidateQueries(['getTechJobsList', 'getPropertyJobs']);
    },
  });
};
//********************** Property Jobs **********************/

//  get property jobs
const getPropertyJobs = async (
  params: IPropertySettings.IGetPropertyJobsParams
): Promise<IPropertySettings.IGetPropertyJobsResponse> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/settings/company/property/jobs`,
    { params }
  );
  return data;
};

export const useGetPropertyJobs = (
  params: IPropertySettings.IGetPropertyJobsParams
) => {
  return useQuery({
    queryKey: ['getPropertyJobs', params],
    queryFn: () => getPropertyJobs(params),
    enabled:
      !!params.cropId &&
      !!params.propertyId &&
      !!params.pageNumber &&
      !!params.pageSize,
  });
};

//  get crop jobs
const getCropJobs = async (params: IPropertySettings.IGetCropJobsParams) => {
  const { data } =
    await axiosClient.get<IPropertySettings.IGetCropJobsResponse>(
      '/company/portalInsights/settings/jobs',
      { params }
    );
  return {
    items: data.jobs,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.totalCount / params.pageSize),
    totalItems: data.totalCount,
    nextPage:
      params?.pageNumber * params?.pageSize < data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetCropJobs = (
  params: IPropertySettings.IGetCropJobsParams
) => {
  return useInfiniteQuery({
    queryKey: ['getCropJobs', params],
    queryFn: ({ pageParam }) =>
      getCropJobs({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled:
      !!params.cropId &&
      !!params.pageNumber &&
      !!params.pageSize &&
      !!params.type,
  });
};

//  Add property job
const addPropertyJob = async (obj: IPropertySettings.IAddPropertyJobDTO) => {
  const { data } = await axiosClient.post(
    '/company/portalInsights/settings/company/property/jobs',
    obj
  );
  return data;
};

export const useAddPropertyJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addPropertyJob,
    onSuccess: () => {
      queryClient.invalidateQueries(['getPropertyJobs']);
    },
  });
};

//********************** Property Configurations **********************/

//  Get property COnfigurations
const getPropertyConfigurations = async (
  entityId: number | undefined
): Promise<IPropertySettings.IPropertyConfiguration> => {
  const { data } = await axiosClient.get(
    '/company/portalInsights/settings/company/property/configurations',
    {
      params: { entityId },
    }
  );
  return data;
};

export const useGetPropertyConfigurations = (entityId: number | undefined) => {
  return useQuery({
    queryKey: ['getPropertyConfigurations', entityId],
    queryFn: () => getPropertyConfigurations(entityId),
    enabled: !!entityId,
  });
};

//  Get measurement units
const getMeasurementUnits = async (
  params: IPropertySettings.IGetMeasurementUnitsParams
) => {
  const { data } =
    await axiosClient.get<IPropertySettings.IGetMeasurementUnitsResponse>(
      '/company/portalInsights/settings/measurement-units',
      { params }
    );
  return {
    items: data.list,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.count / params.pageSize),
    totalItems: data.count,
    nextPage:
      params?.pageNumber * params?.pageSize < data?.count
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetMeasurementUnits = (
  params: IPropertySettings.IGetMeasurementUnitsParams
) => {
  return useInfiniteQuery({
    queryKey: ['getMeasurementUnits'],
    queryFn: ({ pageParam }) =>
      getMeasurementUnits({
        entityId: params.entityId,
        pageNumber: pageParam ?? params.pageNumber,
        pageSize: params.pageSize,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!params.entityId && !!params.pageNumber && !!params.pageSize,
  });
};

//  Update Property Configurations
const updatePropertyConfigurations = async (
  obj: IPropertySettings.IUpdatePropertyConfigurationDTO
) => {
  const { data } = await axiosClient.patch(
    '/company/portalInsights/settings/company/property/configurations',
    obj
  );
  return data;
};

export const useUpdatePropertyConfigurations = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePropertyConfigurations,
    onSuccess: () => {
      queryClient.invalidateQueries(['getPropertyConfigurations']);
      queryClient.invalidateQueries(['getEntities']);
    },
  });
};

//*************************************** Warehouses ******************************

//  Get active countries
export const useGetActiveCountries = (enabled?: boolean) => {
  const getActiveCountries =
    async (): Promise<IPropertySettings.IGetActiveCountriesResponse> => {
      const response = await axiosClient.get(
        '/inventory/api/v1/active-countries'
      );
      return response.data;
    };
  return useQuery({
    queryKey: ['activeCountries'],
    queryFn: getActiveCountries,
    enabled: !!enabled,
  });
};

//  Get warehouse categories
export const useGetWarehouseCategories = (countryId?: number) => {
  const getWarehouseCategories =
    async (): Promise<IPropertySettings.IGetWarehouseCategoriesResponse> => {
      const response = await axiosClient.get(
        `/inventory/api/v2/categories/${countryId}`,
        {
          params: {
            paginated: false,
          },
        }
      );
      return response.data;
    };
  return useQuery({
    queryKey: ['warehouseCategories', countryId],
    queryFn: getWarehouseCategories,
    enabled: !!countryId,
  });
};

//  Get warehouse specialists
export const useGetWarehouseSpecialists = (entityId?: number) => {
  const getWarehouseSpecalist =
    async (): Promise<IPropertySettings.GetWarehouseSpecialistsResponse> => {
      const response = await axiosClient.get(
        `/inventory/api/v1/warehouses/responsible/${entityId}`
      );
      return response.data;
    };
  return useQuery({
    queryKey: ['warehouseSpecialists', entityId],
    queryFn: getWarehouseSpecalist,
    enabled: !!entityId,
  });
};

//  Add warehouse
export const useAddWarehouse = () => {
  const queryClient = useQueryClient();
  const addWarehouse = async (
    warehouse: IPropertySettings.IAddWarehouseSchema
  ) => {
    const response = await axiosClient.post(
      '/inventory/api/v1/warehouses',
      warehouse
    );
    return response.data;
  };
  return useMutation({
    mutationFn: addWarehouse,
    onSuccess: () => {
      queryClient.invalidateQueries(['getWarehouses']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
    },
  });
};

// edit warehouse
const editWarehouse = async ({
  entityId,
  warehouseId,
  ...obj
}: IPropertySettings.IEditWarehouseSchema) => {
  const { data } = await axiosClient.put(
    `inventory/api/v1/warehouses/entity/${entityId}/warehouse/${warehouseId}`,
    obj
  );
  return data;
};

export const useEditWarehouse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editWarehouse,
    onSuccess: () => {
      queryClient.invalidateQueries(['getWarehouses']);
    },
  });
};

// archive warehouse
const archiveWarehouse = async (
  params: IWarehouseManagement.IWarehouseToEntityParams
) => {
  const { data } = await axiosClient.patch(
    `/inventory/api/v1/warehouses/entity/${params.entityId}/warehouse/${params.warehouseId}`
  );
  return data;
};

export const useArchiveWarehouse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: archiveWarehouse,
    onSuccess: () => {
      queryClient.invalidateQueries(['getWarehouses']);
    },
  });
};

// get archived warehouses
const getArchivedWarehouses = async ({
  entityId,
  params,
}: IWarehouseManagement.IEntityParamsSchema): Promise<IPropertySettings.IGetArchivedWarehousesResponses> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/warehouses/entity/${entityId}/deactivated`,
    { params }
  );
  return data;
};

export const useGetArchivedWarehouses = ({
  isInventoryActive,
  entityId,
  ...params
}: IWarehouseManagement.IGetWarehousesParams) => {
  return useQuery({
    queryKey: ['getArchivedWarehouses', entityId, isInventoryActive, params],
    queryFn: () => getArchivedWarehouses({ entityId, params }),
    enabled: !!entityId && !!isInventoryActive,
  });
};
